import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import SpeakerButton from '@/components/SpeakerButton';
import PronunciationQualityBanner from '@/components/PronunciationQualityBanner';
import PartnerAdRotator from '@/components/PartnerAdRotator';
import UpgradeNudge from '@/components/UpgradeNudge';
import { Check, Trophy, ChevronLeft, RefreshCw, HelpCircle, Eye, AlertTriangle, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Free-tier (subscription_status === 'none' or null) sees paywalled hint UX.
function isFreeTier(user) {
    if (!user) return false;
    const s = user.subscription_status || 'none';
    return s === 'none';
}

const MISSING_VOWEL_FREE_ATTEMPT_CAP = 6;
const CRYPTOGRAM_FREE_HINTS = 1;

// ---------------- Shared hook ----------------
function usePuzzle(language, theme, ageTier, puzzleType, seed) {
    const { children, activeChildId } = useAuth();
    const [puzzle, setPuzzle] = useState(null);
    const [err, setErr] = useState(null);
    const activeChild = children?.find?.((c) => c.id === activeChildId) || children?.[0];
    const difficulty = activeChild?.preferred_difficulty || null;
    useEffect(() => {
        setPuzzle(null); setErr(null);
        api.get('/play/puzzle', { params: { language, theme, age_tier: ageTier, puzzle_type: puzzleType, seed, ...(difficulty ? { difficulty } : {}) } })
            .then(({ data }) => setPuzzle(data))
            .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load'));
    }, [language, theme, ageTier, puzzleType, seed, difficulty]);
    return { puzzle, err };
}

function Paywall() {
    return (
        <main className="max-w-3xl mx-auto px-6 py-16" data-testid="paywall-page">
            <div className="bg-lq-peach-100 border border-lq-peach-300 rounded-2xl p-6">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Unlock this puzzle</h2>
                <p className="mt-2 text-lq-ink-700">This puzzle type requires a free account or paid subscription. Word Search in Shona stays free forever.</p>
                <div className="mt-4 flex gap-3">
                    <Link to="/register" className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold">Start free</Link>
                    <Link to="/pricing" className="px-5 py-2 rounded-full border border-lq-ink-100">See plans</Link>
                </div>
            </div>
        </main>
    );
}

function PuzzleFrame({ puzzle, seed, setSeed, children, testId, intro }) {
    const { language } = useParams();
    const TIER_LABEL = {
        seedlings: 'Seedlings · ages 4–7',
        growers: 'Growers · ages 8–11',
        explorers: 'Explorers · ages 12–15',
        teens: 'Teens · ages 16+',
    };
    return (
        <main className="max-w-4xl mx-auto px-6 py-10" data-testid={testId}>
            <Link to={`/languages/${language}`} className="text-sm text-lq-ink-500 inline-flex items-center gap-1 hover:text-lq-purple-700">
                <ChevronLeft size={14}/> Back
            </Link>
            <div className="mt-3 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">{puzzle.title}</h1>
                    <p className="text-sm text-lq-ink-500">{puzzle.language_name} · {TIER_LABEL[puzzle.age_tier_slug] || puzzle.age_tier_slug}</p>
                </div>
                <button onClick={() => setSeed(Math.floor(Math.random() * 1e6))}
                    className="inline-flex items-center gap-1 text-sm text-lq-purple-700 hover:text-lq-purple-900" data-testid="new-puzzle">
                    <RefreshCw size={14}/> New puzzle
                </button>
            </div>
            {intro && (
                <p className="mt-2 text-sm text-lq-ink-700 max-w-2xl" data-testid="puzzle-intro">{intro}</p>
            )}
            <div className="mt-3"><PronunciationQualityBanner languageSlug={language} /></div>
            {children}
            <UpgradeNudge />
            <PartnerAdRotator />
        </main>
    );
}

// ==================================================
// WORD SEARCH
// ==================================================
function pointOnLine(r, c, start, end) {
    const [sr, sc] = start, [er, ec] = end;
    const dr = Math.sign(er - sr), dc = Math.sign(ec - sc);
    const steps = Math.max(Math.abs(er - sr), Math.abs(ec - sc));
    for (let i = 0; i <= steps; i++) if (sr + dr * i === r && sc + dc * i === c) return true;
    return false;
}

export function WordSearch() {
    const { language, theme, ageTier } = useParams();
    const { user } = useAuth();
    const [seed, setSeed] = useState(42);
    const { puzzle, err } = usePuzzle(language, theme, ageTier, 'word_search', seed);
    const [selecting, setSelecting] = useState(null);
    const [hoverCell, setHoverCell] = useState(null);
    const [foundIds, setFoundIds] = useState(new Set());
    const [recentFind, setRecentFind] = useState(null);
    const [wrongFlash, setWrongFlash] = useState(false);
    const [startedAt] = useState(Date.now());
    const [savedProgress, setSavedProgress] = useState(false);

    const placed = useMemo(() => puzzle?.payload?.placed || [], [puzzle]);
    const size = puzzle?.payload?.size || 12;
    const allFound = placed.length > 0 && foundIds.size >= placed.length;

    useEffect(() => { setFoundIds(new Set()); setRecentFind(null); setSavedProgress(false); }, [seed]);

    const getSelectionCells = () => {
        if (!selecting || !hoverCell) return [];
        const [r0, c0] = [selecting.r, selecting.c];
        const [r1, c1] = [hoverCell.r, hoverCell.c];
        const dr = Math.sign(r1 - r0), dc = Math.sign(c1 - c0);
        const absR = Math.abs(r1 - r0), absC = Math.abs(c1 - c0);
        if (absR !== 0 && absC !== 0 && absR !== absC) return [];
        const steps = Math.max(absR, absC);
        const cells = [];
        for (let i = 0; i <= steps; i++) cells.push([r0 + dr * i, c0 + dc * i]);
        return cells;
    };

    const onSelectionEnd = () => {
        if (!selecting || !hoverCell) return;
        const cells = getSelectionCells();
        if (cells.length < 2) { setSelecting(null); return; }
        const chars = cells.map(([r, c]) => puzzle.payload.grid[r][c]).join('');
        const rev = chars.split('').reverse().join('');
        const m = placed.find((p) => (p.token === chars || p.token === rev) && !foundIds.has(p.word_id));
        if (m) {
            const next = new Set(foundIds); next.add(m.word_id); setFoundIds(next);
            setRecentFind(m);
            api.get(`/audio/word/${m.word_id}`).then(({ data }) => {
                if (data.cdn_url) new Audio((process.env.REACT_APP_BACKEND_URL || '') + data.cdn_url).play().catch(() => {});
            }).catch(() => {});
            if (user) api.post('/play/vocabulary', { word_id: m.word_id }).catch(() => {});
        } else {
            setWrongFlash(true); setTimeout(() => setWrongFlash(false), 400);
        }
        setSelecting(null);
    };

    const cellInSelection = (r, c) => getSelectionCells().some(([rr, cc]) => rr === r && cc === c);
    const cellFound = (r, c) => placed.some((p) => foundIds.has(p.word_id) && pointOnLine(r, c, p.start, p.end));

    const { children, activeChildId } = useAuth();
    const [pointsAwarded, setPointsAwarded] = useState(null);

    useEffect(() => {
        if (allFound && !savedProgress && user) {
            const timeSec = Math.round((Date.now() - startedAt) / 1000);
            const ids = Array.from(foundIds);
            const child = children?.find?.((c) => c.id === activeChildId);
            api.post('/play/complete', {
                puzzle_id: puzzle.id,
                puzzle_type: 'word_search',
                language_slug: puzzle.language_slug,
                difficulty: child?.preferred_difficulty,
                words_found: ids.length,
                time_seconds: timeSec,
                accuracy_pct: 100,
                learned_word_ids: ids,
                child_profile_id: child?.id,
            })
                .then(({ data }) => { setSavedProgress(true); setPointsAwarded(data?.points_awarded || 0); })
                .catch(() => setSavedProgress(true));
        }
    }, [allFound, savedProgress, foundIds, puzzle, user, startedAt, children, activeChildId]);

    if (err === 'Sign in or subscribe to play this puzzle type') return <Paywall/>;
    if (err) return <main className="max-w-4xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!puzzle) return <main className="max-w-4xl mx-auto px-6 py-16 text-lq-ink-500">Loading…</main>;

    return (
        <PuzzleFrame puzzle={puzzle} seed={seed} setSeed={setSeed} testId="page-play-wordsearch"
            intro={`Find each ${puzzle.language_name} word in the grid. Drag across the letters — up, down, sideways or diagonally. Tap the speaker after a word is found to hear how it sounds.`}
        >
            <p className="text-sm text-lq-ink-500 -mt-3 mb-4">{placed.length} words to find</p>
            <div className="h-2 bg-lq-ink-100 rounded-full overflow-hidden mb-6">
                <div className="h-full bg-lq-peach-500 transition-all" style={{ width: `${Math.min(100, (foundIds.size / Math.max(1, placed.length)) * 100)}%` }} />
            </div>
            <div className="grid md:grid-cols-[1.3fr,1fr] gap-6">
                <div className={`bg-white border-2 ${wrongFlash ? 'border-lq-error shake' : 'border-lq-ink-100'} rounded-2xl p-4 select-none`}
                    onPointerLeave={() => { setSelecting(null); setHoverCell(null); }}>
                    <div
                        className="grid gap-1"
                        style={{ gridTemplateColumns: `repeat(${size}, 1fr)`, touchAction: 'none' }}
                        data-testid="wordsearch-grid"
                        onPointerDown={(e) => {
                            const cd = e.target?.dataset?.cell;
                            if (!cd) return;
                            try { e.target.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
                            const [r, c] = cd.split(',').map(Number);
                            setSelecting({ r, c, pointerId: e.pointerId, captureEl: e.target });
                            setHoverCell({ r, c });
                        }}
                        onPointerMove={(e) => {
                            if (!selecting) return;
                            // Use elementFromPoint so dragging works for both mouse and touch,
                            // including diagonals — we don't axis-lock, we just resolve which
                            // cell the pointer is currently over each frame.
                            const el = document.elementFromPoint(e.clientX, e.clientY);
                            const cd = el?.dataset?.cell;
                            if (!cd) return;
                            const [rr, cc] = cd.split(',').map(Number);
                            if (!hoverCell || hoverCell.r !== rr || hoverCell.c !== cc) {
                                setHoverCell({ r: rr, c: cc });
                            }
                        }}
                        onPointerUp={(e) => {
                            try { selecting?.captureEl?.releasePointerCapture?.(selecting.pointerId); } catch { /* ignore */ }
                            onSelectionEnd();
                        }}
                        onPointerCancel={() => { setSelecting(null); setHoverCell(null); }}
                    >
                        {puzzle.payload.grid.map((row, r) => row.map((ch, c) => {
                            const isFound = cellFound(r, c);
                            const isSel = cellInSelection(r, c);
                            const cls = `aspect-square flex items-center justify-center rounded-md text-base md:text-lg font-display font-semibold border cursor-pointer transition-colors ${
                                isFound ? 'cell-found border-lq-peach-500' :
                                isSel ? 'cell-selected border-lq-purple-500' :
                                'border-lq-ink-100 bg-lq-paper hover:bg-lq-purple-50'
                            }`;
                            return (
                                <div key={`${r}-${c}`} className={cls}
                                    data-cell={`${r},${c}`} data-testid={`cell-${r}-${c}`}>{ch}</div>
                            );
                        }))}
                    </div>
                </div>
                <div>
                    <h2 className="font-display text-lg font-semibold text-lq-ink-900 mb-3">Find these words</h2>
                    <ul className="bg-white border border-lq-ink-100 rounded-2xl divide-y divide-lq-ink-100" data-testid="wordsearch-wordlist">
                        {placed.map((p) => {
                            const found = foundIds.has(p.word_id);
                            return (
                                <li key={p.word_id} className={`flex items-center justify-between px-4 py-2 ${found ? 'bg-lq-peach-100' : ''}`}>
                                    <div>
                                        <div className={`font-display font-semibold ${found ? 'line-through text-lq-ink-500' : 'text-lq-ink-900'}`}>{p.foreign_word}</div>
                                        <div className="text-xs text-lq-ink-500">= {p.english_translation}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {found && <SpeakerButton wordId={p.word_id} text={p.foreign_word} languageSlug={language} size={16}/>}
                                        {found && <Check size={16} className="text-lq-success"/>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    {recentFind && (
                        <div className="mt-4 p-4 rounded-xl bg-lq-peach-100 border border-lq-peach-300 pulse-success" data-testid="recent-find">
                            <div className="text-xs uppercase tracking-wider font-semibold text-lq-peach-600">You found</div>
                            <div className="font-display text-2xl text-lq-ink-900 mt-1">{recentFind.foreign_word}</div>
                            <div className="text-sm text-lq-ink-700">→ {recentFind.english_translation}</div>
                        </div>
                    )}
                </div>
            </div>
            {allFound && <CompletionBanner count={placed.length} pointsAwarded={pointsAwarded} />}
        </PuzzleFrame>
    );
}

function CompletionBanner({ count, pointsAwarded }) {
    return (
        <section className="mt-10 bg-gradient-to-br from-lq-purple-100 to-lq-peach-100 border-2 border-lq-purple-300 rounded-3xl p-8 text-center" data-testid="puzzle-complete">
            <div className="flex justify-center"><Trophy size={48} className="text-lq-peach-600"/></div>
            <h2 className="mt-4 font-display text-3xl font-semibold text-lq-ink-black">You found them all! 🎉</h2>
            <p className="mt-2 text-lq-ink-700">Brilliant work — {count} new words are now in your vocabulary box. Tap the little speaker on each word to practise saying them out loud with someone in your family.</p>
            {pointsAwarded > 0 && (
                <p className="mt-3 inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full font-display font-semibold text-lq-ink-900" data-testid="points-awarded-banner">
                    + {pointsAwarded} points
                </p>
            )}
        </section>
    );
}

// ==================================================
// BALLOON POP (replaces Hangman, kid-safe)
// ==================================================
const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function BalloonPop() {
    const { language, theme, ageTier } = useParams();
    const [seed, setSeed] = useState(Math.floor(Math.random() * 1000));
    const { puzzle, err } = usePuzzle(language, theme, ageTier, 'balloon_pop', seed);
    const [guessed, setGuessed] = useState(new Set());

    useEffect(() => { setGuessed(new Set()); }, [seed]);

    if (err === 'Sign in or subscribe to play this puzzle type') return <Paywall/>;
    if (err) return <main className="max-w-4xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!puzzle) return <main className="max-w-4xl mx-auto px-6 py-16 text-lq-ink-500">Loading…</main>;

    const answer = puzzle.payload.answer;
    const total = puzzle.payload.balloons_total;
    const wrong = Array.from(guessed).filter((g) => !answer.toUpperCase().includes(g)).length;
    const remainingBalloons = Math.max(0, total - wrong);
    const lost = remainingBalloons === 0;
    const won = answer && answer.toUpperCase().split('').every((ch) => !ch.match(/[A-Z]/i) || guessed.has(ch));

    const guess = (L) => { if (won || lost) return; const n = new Set(guessed); n.add(L); setGuessed(n); };
    const masked = answer.toUpperCase().split('').map((ch) => !ch.match(/[A-Z]/i) ? ch : (guessed.has(ch) ? ch : '_')).join(' ');

    const BALLOON_COLORS = ['#F5A67C', '#7B3FA9', '#87CEEB', '#F3B7A4', '#D97F58', '#3FA4CF'];

    return (
        <PuzzleFrame puzzle={puzzle} seed={seed} setSeed={setSeed} testId="page-play-balloonpop"
            intro="Guess the letters before the balloons pop. Tap a letter — if it's in the word, it pops into place. Wrong guesses pop a balloon!"
        >
            <div className="bg-white border-2 border-lq-ink-100 rounded-3xl p-8 text-center">
                <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500">Save the balloons</div>
                <div className="text-sm text-lq-ink-500 mt-1 flex items-center justify-center gap-1"><HelpCircle size={14}/> Clue: <span className="text-lq-ink-900 font-medium">{puzzle.payload.hint}</span></div>

                {/* Balloons */}
                <div className="mt-6 flex justify-center gap-3 h-20" aria-label={`${remainingBalloons} balloons remaining`}>
                    {Array.from({ length: total }).map((_, i) => {
                        const popped = i >= remainingBalloons;
                        const color = BALLOON_COLORS[i % BALLOON_COLORS.length];
                        return (
                            <div key={i} className={`relative transition-all duration-300 ${popped ? 'opacity-20 scale-75' : 'scale-100'}`}>
                                <svg width="40" height="56" viewBox="0 0 40 56" aria-hidden="true">
                                    <ellipse cx="20" cy="18" rx="14" ry="18" fill={popped ? '#C7C1D1' : color} />
                                    <path d="M20 36 L17 40 L23 40 Z" fill={popped ? '#C7C1D1' : color} />
                                    <path d="M20 40 Q23 45 18 50 Q22 54 20 56" stroke="#6B6378" strokeWidth="1" fill="none" />
                                    {popped && <text x="20" y="24" fontSize="16" textAnchor="middle" fill="#B91C1C">✗</text>}
                                </svg>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 font-display text-4xl md:text-5xl tracking-widest text-lq-purple-700" data-testid="balloonpop-word">{masked}</div>

                <div className="mt-8 grid grid-cols-9 gap-1.5 max-w-lg mx-auto">
                    {ALPHA.map((L) => {
                        const used = guessed.has(L);
                        const correct = used && answer.toUpperCase().includes(L);
                        return (
                            <button key={L} onClick={() => guess(L)} disabled={used || won || lost}
                                className={`py-2 rounded-md text-sm font-bold transition-colors ${
                                    used
                                        ? (correct ? 'bg-lq-peach-300 text-lq-ink-900' : 'bg-lq-ink-100 text-lq-ink-500')
                                        : 'bg-lq-purple-50 text-lq-purple-900 hover:bg-lq-purple-500 hover:text-white'
                                }`}
                                data-testid={`balloonpop-key-${L}`}>{L}</button>
                        );
                    })}
                </div>
            </div>
            {(won || lost) && (
                <section className="mt-6 bg-lq-purple-100 border-2 border-lq-purple-300 rounded-2xl p-6 text-center" data-testid="balloonpop-result">
                    <h2 className="font-display text-2xl font-semibold text-lq-ink-900">
                        {won ? `Balloons saved! ${remainingBalloons}/${total} survived 🎉` : `Out of balloons! The word was ${puzzle.payload.foreign_word}.`}
                    </h2>
                    <p className="mt-1 text-lq-ink-700">{puzzle.payload.foreign_word} → {puzzle.payload.english_translation}</p>
                    <div className="mt-3 flex justify-center">
                        <SpeakerButton wordId={puzzle.payload.word_id} text={puzzle.payload.foreign_word} languageSlug={language} size={20}/>
                    </div>
                </section>
            )}
        </PuzzleFrame>
    );
}

// ==================================================
// CROSSWORD
// ==================================================
export function Crossword() {
    const { language, theme, ageTier } = useParams();
    const [seed, setSeed] = useState(42);
    const { puzzle, err } = usePuzzle(language, theme, ageTier, 'crossword', seed);
    const [entries, setEntries] = useState({});
    const inputRefs = React.useRef({});
    useEffect(() => { setEntries({}); }, [seed]);
    if (err === 'Sign in or subscribe to play this puzzle type') return <Paywall/>;
    if (err) return <main className="max-w-4xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!puzzle) return <main className="max-w-4xl mx-auto px-6 py-16 text-lq-ink-500">Loading…</main>;

    const size = puzzle.payload.size;
    const grid = puzzle.payload.grid;
    const placed = puzzle.payload.placed || [];
    const setCell = (r, c, v) => setEntries((e) => ({ ...e, [`${r}-${c}`]: v.toUpperCase().slice(0, 1) }));
    const checkWord = (p) => {
        const dr = p.direction === 'down' ? 1 : 0;
        const dc = p.direction === 'across' ? 1 : 0;
        return p.token.split('').every((ch, i) => (entries[`${p.start[0] + dr * i}-${p.start[1] + dc * i}`] || '') === ch);
    };

    // Continuous delete: when Backspace is pressed, clear the current cell
    // and jump focus to the previous letter cell on the same word axis. This
    // makes erasing a wrong word feel as fast as typing it.
    const focusCell = (r, c) => {
        const ref = inputRefs.current[`${r}-${c}`];
        if (ref) ref.focus();
    };
    const previousCell = (r, c) => {
        // Try left first (across), then up (down). Skip black squares.
        for (const [dr, dc] of [[0, -1], [-1, 0]]) {
            const rr = r + dr, cc = c + dc;
            if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
            if (grid[rr][cc] === '.') continue;
            return [rr, cc];
        }
        return null;
    };
    const nextCell = (r, c) => {
        for (const [dr, dc] of [[0, 1], [1, 0]]) {
            const rr = r + dr, cc = c + dc;
            if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
            if (grid[rr][cc] === '.') continue;
            return [rr, cc];
        }
        return null;
    };
    const onKeyDown = (e, r, c) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const had = !!entries[`${r}-${c}`];
            setEntries((prev) => { const n = { ...prev }; delete n[`${r}-${c}`]; return n; });
            // If the cell was already empty, jump back AND clear the previous
            // cell — gives a true continuous "hold-to-delete" feel even on
            // single backspace presses.
            if (!had) {
                const prev = previousCell(r, c);
                if (prev) {
                    setEntries((p) => { const n = { ...p }; delete n[`${prev[0]}-${prev[1]}`]; return n; });
                    focusCell(prev[0], prev[1]);
                }
            }
            return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            const map = { ArrowRight: [0, 1], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowUp: [-1, 0] };
            const [dr, dc] = map[e.key];
            let rr = r + dr, cc = c + dc;
            while (rr >= 0 && cc >= 0 && rr < size && cc < size && grid[rr][cc] === '.') { rr += dr; cc += dc; }
            if (rr >= 0 && cc >= 0 && rr < size && cc < size) { e.preventDefault(); focusCell(rr, cc); }
        }
    };
    const onChangeCell = (r, c, raw) => {
        const v = (raw || '').toUpperCase().slice(0, 1);
        setCell(r, c, v);
        if (v) {
            const nx = nextCell(r, c);
            if (nx) focusCell(nx[0], nx[1]);
        }
    };

    return (
        <PuzzleFrame puzzle={puzzle} seed={seed} setSeed={setSeed} testId="page-play-crossword"
            intro="Read each English clue, then type the letters. Use Tab or arrow keys to jump. Press Backspace twice to wipe a wrong word fast."
        >
            <div className="grid md:grid-cols-[auto,1fr] gap-6">
                <div className="bg-white border-2 border-lq-ink-100 rounded-2xl p-4 overflow-x-auto">
                    <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${size}, 2.25rem)`, gridAutoRows: '2.25rem' }} data-testid="crossword-grid">
                        {grid.map((row, r) => row.map((ch, c) => {
                            const isCell = ch !== '.';
                            const number = placed.find((p) => p.start[0] === r && p.start[1] === c)?.number;
                            if (!isCell) return <div key={`${r}-${c}`} className="bg-lq-ink-black rounded-sm" />;
                            const val = entries[`${r}-${c}`] || '';
                            const correct = val && val === ch;
                            return (
                                <div key={`${r}-${c}`} className="relative w-9 h-9">
                                    {number && <span className="absolute top-0 left-1 text-[10px] font-bold text-lq-ink-500 z-10 pointer-events-none">{number}</span>}
                                    <input value={val}
                                        ref={(el) => { inputRefs.current[`${r}-${c}`] = el; }}
                                        onChange={(e) => onChangeCell(r, c, e.target.value)}
                                        onKeyDown={(e) => onKeyDown(e, r, c)}
                                        inputMode="text"
                                        autoCapitalize="characters"
                                        autoComplete="off"
                                        className={`absolute inset-0 w-full h-full text-center font-display font-semibold text-lg uppercase rounded-sm border ${
                                            correct ? 'bg-lq-peach-300 border-lq-peach-500' : 'bg-lq-paper border-lq-ink-300 focus:border-lq-purple-500'
                                        }`}
                                        data-testid={`cw-cell-${r}-${c}`}/>
                                </div>
                            );
                        }))}
                    </div>
                </div>
                <div>
                    <h2 className="font-display text-lg font-semibold text-lq-ink-900">Clues</h2>
                    <div className="mt-3 grid sm:grid-cols-2 gap-4">
                        {['across', 'down'].map((dir) => (
                            <div key={dir}>
                                <h3 className="uppercase tracking-wider text-xs font-semibold text-lq-purple-700">{dir}</h3>
                                <ul className="mt-2 text-sm space-y-2" data-testid={`clues-${dir}`}>
                                    {placed.filter((p) => p.direction === dir).map((p) => {
                                        const ok = checkWord(p);
                                        return (
                                            <li key={p.number} className={`flex items-start gap-2 ${ok ? 'text-lq-success' : 'text-lq-ink-900'}`}>
                                                <span className="font-semibold">{p.number}.</span>
                                                <span>{p.hint} <span className="text-lq-ink-500">({p.length})</span></span>
                                                {ok && <Check size={14} className="mt-0.5"/>}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </PuzzleFrame>
    );
}

// ==================================================
// WORD SCRAMBLE
// ==================================================
export function WordScramble() {
    const { language, theme, ageTier } = useParams();
    const [seed, setSeed] = useState(Math.floor(Math.random() * 1000));
    const { puzzle, err } = usePuzzle(language, theme, ageTier, 'word_scramble', seed);
    const [picked, setPicked] = useState([]);

    useEffect(() => { setPicked([]); }, [seed]);
    if (err === 'Sign in or subscribe to play this puzzle type') return <Paywall/>;
    if (err) return <main className="max-w-4xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!puzzle) return <main className="max-w-4xl mx-auto px-6 py-16 text-lq-ink-500">Loading…</main>;

    const letters = puzzle.payload.scrambled_letters;
    const answer = puzzle.payload.answer;
    const guess = picked.map((i) => letters[i]).join('');
    const won = guess === answer;

    const toggle = (i) => {
        if (won) return;
        setPicked((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i]);
    };

    return (
        <PuzzleFrame puzzle={puzzle} seed={seed} setSeed={setSeed} testId="page-play-scramble"
            intro="The letters are jumbled up. Tap them in the right order to spell the word. Tap again to take a letter back."
        >
            <div className="bg-white border-2 border-lq-ink-100 rounded-3xl p-8 text-center">
                <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500">Unscramble the word</div>
                <div className="mt-1 text-sm text-lq-ink-500">English hint: <span className="text-lq-ink-900 font-medium">{puzzle.payload.hint}</span></div>
                <div className="mt-6 min-h-[3rem] font-display text-4xl text-lq-purple-700 tracking-widest" data-testid="scramble-guess">
                    {guess || <span className="text-lq-ink-300">?</span>}
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {letters.map((L, i) => {
                        const used = picked.includes(i);
                        return (
                            <button key={i} onClick={() => toggle(i)} disabled={won}
                                className={`w-12 h-12 font-display text-xl rounded-lg border-2 transition-colors ${
                                    used ? 'bg-lq-ink-100 border-lq-ink-100 text-lq-ink-300' : 'bg-lq-purple-50 border-lq-purple-300 text-lq-purple-900 hover:bg-lq-purple-500 hover:text-white'
                                }`}
                                data-testid={`scramble-letter-${i}`}>{L}</button>
                        );
                    })}
                </div>
                <button onClick={() => setPicked([])} className="mt-4 text-sm text-lq-ink-500 hover:text-lq-purple-700" data-testid="scramble-clear">Clear</button>
                {won && (
                    <div className="mt-6 p-5 rounded-2xl bg-lq-peach-100 border border-lq-peach-300" data-testid="scramble-win">
                        <div className="font-display text-2xl text-lq-ink-900">{puzzle.payload.foreign_word} → {puzzle.payload.english_translation}</div>
                        <div className="mt-2 flex justify-center"><SpeakerButton wordId={puzzle.payload.word_id} text={puzzle.payload.foreign_word} languageSlug={language} size={20}/></div>
                    </div>
                )}
            </div>
        </PuzzleFrame>
    );
}

// ==================================================
// MISSING VOWEL
// ==================================================
export function MissingVowel() {
    const { language, theme, ageTier } = useParams();
    const { user } = useAuth();
    const [seed, setSeed] = useState(Math.floor(Math.random() * 1000));
    const { puzzle, err } = usePuzzle(language, theme, ageTier, 'missing_vowel', seed);
    const [value, setValue] = useState('');
    // Free-tier attempts are committed only when the user explicitly checks
    // (Submit), so typing freely doesn't burn through the 6-attempt budget.
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [feedback, setFeedback] = useState(null); // 'wrong' | 'correct' | null
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        // reset all per-puzzle state when seed changes
        setValue('');
        setWrongAttempts(0);
        setFeedback(null);
        setSubmitted(false);
    }, [seed]);

    if (err === 'Sign in or subscribe to play this puzzle type') return <Paywall/>;
    if (err) return <main className="max-w-4xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!puzzle) return <main className="max-w-4xl mx-auto px-6 py-16 text-lq-ink-500">Loading…</main>;

    const answer = puzzle.payload.answer;
    const free = isFreeTier(user);
    const won = submitted && feedback === 'correct';
    const capped = free && wrongAttempts >= MISSING_VOWEL_FREE_ATTEMPT_CAP;

    const onSubmit = () => {
        if (won || capped || !value.trim()) return;
        const correct = value.trim().toLowerCase() === answer.toLowerCase();
        setSubmitted(true);
        if (correct) {
            setFeedback('correct');
        } else {
            setFeedback('wrong');
            if (free) setWrongAttempts((n) => n + 1);
            // After a brief flash, allow another go (unless capped)
            setTimeout(() => { setSubmitted(false); setFeedback(null); }, 1100);
        }
    };

    const attemptsLeft = Math.max(0, MISSING_VOWEL_FREE_ATTEMPT_CAP - wrongAttempts);

    return (
        <PuzzleFrame puzzle={puzzle} seed={seed} setSeed={setSeed} testId="page-play-missingvowel"
            intro="A few vowels (a, e, i, o, u) have run away. Type the whole word with the missing vowels in the right places."
        >
            <div className="bg-white border-2 border-lq-ink-100 rounded-3xl p-8 text-center">
                <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500">Fill in the missing vowels</div>
                <div className="mt-1 text-sm text-lq-ink-500">Hint: <span className="text-lq-ink-900 font-medium">{puzzle.payload.hint}</span></div>
                <div className="mt-6 font-display text-5xl md:text-6xl tracking-widest text-lq-purple-700" data-testid="mv-masked">{puzzle.payload.masked}</div>

                <input value={value} onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
                    disabled={won || capped}
                    className={`mt-8 w-full max-w-sm mx-auto block text-center font-display text-2xl py-3 rounded-xl border-2 transition-colors ${
                        won ? 'border-lq-success bg-lq-purple-50' :
                        feedback === 'wrong' ? 'border-lq-error bg-red-50' :
                        'border-lq-ink-300 focus:border-lq-purple-500'
                    }`}
                    placeholder="type the word"
                    data-testid="mv-input"/>

                {!won && (
                    <button onClick={onSubmit} disabled={capped || !value.trim()}
                        className="mt-4 inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 disabled:bg-lq-ink-100 disabled:text-lq-ink-500 text-white font-semibold px-6 py-2.5 rounded-full"
                        data-testid="mv-submit">
                        Check answer
                    </button>
                )}

                {free && !won && (
                    <div className="mt-4 inline-flex items-center gap-2 text-xs text-lq-ink-700 bg-lq-peach-100 border border-lq-peach-300 rounded-full px-3 py-1" data-testid="mv-attempts-counter">
                        <AlertTriangle size={12} className="text-lq-peach-600" />
                        {attemptsLeft} {attemptsLeft === 1 ? 'attempt' : 'attempts'} left on this puzzle
                    </div>
                )}

                {capped && !won && (
                    <div className="mt-6 p-5 rounded-2xl bg-lq-purple-50 border border-lq-purple-300 text-left max-w-md mx-auto" data-testid="mv-capped">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white text-lq-purple-700 border border-lq-purple-300">
                            <Lock size={18} />
                        </div>
                        <h3 className="mt-3 font-display text-lg font-semibold text-lq-ink-900">That&rsquo;s six tries on this puzzle.</h3>
                        <p className="mt-2 text-sm text-lq-ink-700">
                            Want unlimited attempts and the rest of the puzzle library? Choose a paid plan &mdash; no adverts, every age tier unlocked, up to five child profiles.
                        </p>
                        <div className="mt-4">
                            <Link to="/pricing" className="inline-flex items-center justify-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm" data-testid="mv-upgrade-cta">
                                See plans &amp; pricing
                            </Link>
                            <button onClick={() => setSeed(Math.floor(Math.random() * 1e6))}
                                className="ml-2 inline-flex items-center justify-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-5 py-2.5 rounded-full text-sm"
                                data-testid="mv-try-different-btn">
                                Try a different puzzle
                            </button>
                        </div>
                        <p className="mt-3 text-xs text-lq-ink-500">
                            Got a voucher? <Link to="/redeem" className="text-lq-purple-700 hover:underline">Redeem it here</Link>.
                        </p>
                    </div>
                )}

                {won && (
                    <div className="mt-6 p-5 rounded-2xl bg-lq-peach-100 border border-lq-peach-300" data-testid="mv-win">
                        <div className="font-display text-2xl text-lq-ink-900">{puzzle.payload.foreign_word} → {puzzle.payload.english_translation}</div>
                        <div className="mt-2 flex justify-center"><SpeakerButton wordId={puzzle.payload.word_id} text={puzzle.payload.foreign_word} languageSlug={language} size={20}/></div>
                    </div>
                )}
            </div>
        </PuzzleFrame>
    );
}

// ==================================================
// CRYPTOGRAM
// ==================================================
export function Cryptogram() {
    const { language, theme, ageTier } = useParams();
    const { user } = useAuth();
    const [seed, setSeed] = useState(Math.floor(Math.random() * 1000));
    const { puzzle, err } = usePuzzle(language, theme, ageTier, 'cryptogram', seed);
    const [mapping, setMapping] = useState({}); // cipher letter -> user's guess
    const [adRevealed, setAdRevealed] = useState({}); // cipher letter -> true (locked, hint-revealed)
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => {
        if (puzzle) {
            const initial = {};
            const plain = puzzle.payload.plain_text;
            const cipher = puzzle.payload.cipher_text;
            for (const L of puzzle.payload.revealed_plain_letters || []) {
                for (let i = 0; i < plain.length; i++) {
                    if (plain[i] === L) { initial[cipher[i]] = L; break; }
                }
            }
            setMapping(initial);
            setAdRevealed({});
            setShowUpgrade(false);
        }
    }, [puzzle, seed]);

    if (err === 'Sign in or subscribe to play this puzzle type') return <Paywall/>;
    if (err) return <main className="max-w-4xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!puzzle) return <main className="max-w-4xl mx-auto px-6 py-16 text-lq-ink-500">Loading…</main>;

    const cipher = puzzle.payload.cipher_text;
    const plain = puzzle.payload.plain_text;
    const currentGuess = cipher.split('').map((c) => c.match(/[A-Z]/) ? (mapping[c] || '_') : c).join('');
    const won = currentGuess === plain;
    const free = isFreeTier(user);
    const hintsUsed = Object.keys(adRevealed).length;
    const freeHintsLeft = Math.max(0, CRYPTOGRAM_FREE_HINTS - hintsUsed);

    const setLetter = (cipherChar, val) => {
        const v = (val || '').toUpperCase().slice(0, 1);
        setMapping((m) => ({ ...m, [cipherChar]: v }));
    };

    // Reveal one cipher letter the user hasn't already cracked. Free users get
    // CRYPTOGRAM_FREE_HINTS per puzzle; paid users get unlimited reveals.
    const revealOneLetter = () => {
        if (free && freeHintsLeft <= 0) {
            setShowUpgrade(true);
            return;
        }
        const initialReveals = puzzle.payload.revealed_plain_letters || [];
        for (let i = 0; i < cipher.length; i++) {
            const c = cipher[i];
            if (!c.match(/[A-Z]/)) continue;
            const trueLetter = plain[i];
            if (initialReveals.includes(trueLetter)) continue;
            if (adRevealed[c]) continue;
            setMapping((m) => ({ ...m, [c]: trueLetter }));
            setAdRevealed((r) => ({ ...r, [c]: true }));
            return;
        }
    };

    const chars = Array.from(cipher);
    const remainingPositions = chars.filter((c, i) => {
        if (!c.match(/[A-Z]/)) return false;
        const trueLetter = plain[i];
        const initial = (puzzle.payload.revealed_plain_letters || []).includes(trueLetter);
        return !initial && !adRevealed[c];
    }).length;

    return (
        <PuzzleFrame puzzle={puzzle} seed={seed} setSeed={setSeed} testId="page-play-cryptogram"
            intro="Each letter has been swapped for another. A few are filled in to start you off. Crack the code to reveal the phrase!"
        >
            <div className="bg-white border-2 border-lq-ink-100 rounded-3xl p-6 md:p-8">
                <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500 text-center">Crack the cipher</div>
                <div className="mt-1 text-sm text-lq-ink-500 text-center">Meaning: <em className="text-lq-ink-900">{puzzle.payload.english_translation}</em></div>
                <div className="mt-8 flex flex-wrap justify-center gap-1" data-testid="crypto-letters">
                    {chars.map((c, i) => {
                        if (!c.match(/[A-Z]/)) return <span key={i} className="w-3 font-display text-2xl text-lq-ink-500">{c === ' ' ? '\u00a0' : c}</span>;
                        const val = mapping[c] || '';
                        const initialFixed = (puzzle.payload.revealed_plain_letters || []).includes(val) && plain[i] === val;
                        const adFixed = adRevealed[c] && val === plain[i];
                        const fixed = initialFixed || adFixed;
                        return (
                            <div key={i} className="flex flex-col items-center">
                                <input
                                    value={val}
                                    onChange={(e) => setLetter(c, e.target.value)}
                                    disabled={fixed}
                                    className={`w-8 h-10 text-center font-display uppercase text-lg rounded-sm border ${
                                        val && val === plain[i] ? 'bg-lq-peach-300 border-lq-peach-500' : 'bg-lq-paper border-lq-ink-300 focus:border-lq-purple-500'
                                    } ${initialFixed ? 'bg-lq-purple-50' : ''} ${adFixed ? 'bg-lq-peach-100 border-lq-peach-500' : ''}`}
                                    data-testid={`crypto-cell-${i}`}
                                />
                                <div className="text-[10px] mt-0.5 font-mono text-lq-ink-500">{c}</div>
                            </div>
                        );
                    })}
                </div>

                {!won && remainingPositions > 0 && (
                    <div className="mt-6 flex flex-col items-center gap-2">
                        <button onClick={revealOneLetter}
                            className="inline-flex items-center gap-2 bg-white border-2 border-lq-purple-300 hover:border-lq-purple-500 text-lq-purple-700 font-semibold px-5 py-2 rounded-full text-sm"
                            data-testid="crypto-hint-btn">
                            <Eye size={14} /> {free ? `Reveal a letter (${freeHintsLeft} free hint${freeHintsLeft === 1 ? '' : 's'} left)` : 'Reveal a letter'}
                        </button>
                        {free && freeHintsLeft === 0 && (
                            <p className="text-xs text-lq-ink-500">
                                Out of free hints. <Link to="/pricing" className="text-lq-purple-700 hover:underline">Upgrade for unlimited reveals.</Link>
                            </p>
                        )}
                    </div>
                )}

                {showUpgrade && (
                    <div className="mt-4 p-4 rounded-2xl bg-lq-purple-50 border border-lq-purple-300 max-w-md mx-auto text-center" data-testid="crypto-upgrade-prompt">
                        <p className="text-sm text-lq-ink-900">
                            <strong>One free hint per puzzle on the free plan.</strong> Paid plans give unlimited letter reveals on every puzzle &mdash; no adverts, ever.
                        </p>
                        <div className="mt-3 flex flex-wrap justify-center gap-2">
                            <Link to="/pricing" className="inline-flex items-center justify-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-4 py-2 rounded-full text-sm" data-testid="crypto-upgrade-cta">
                                See plans &amp; pricing
                            </Link>
                            <button onClick={() => setShowUpgrade(false)} className="text-sm text-lq-ink-500 hover:text-lq-ink-900 px-3 py-2">Not now</button>
                        </div>
                    </div>
                )}

                {won && (
                    <div className="mt-8 p-5 rounded-2xl bg-lq-peach-100 border border-lq-peach-300 text-center" data-testid="crypto-win">
                        <div className="font-display text-xl text-lq-ink-900">{puzzle.payload.foreign_phrase}</div>
                        <div className="text-sm text-lq-ink-500 mt-1">→ {puzzle.payload.english_translation}</div>
                        <div className="mt-2 flex justify-center"><SpeakerButton text={puzzle.payload.foreign_phrase} languageSlug={language} size={20}/></div>
                    </div>
                )}
            </div>
        </PuzzleFrame>
    );
}
